import { useState } from 'react';
import { useNavigate } from 'react-router';
import { describeRole, loginSchema, type MeResponse } from 'shared';
import { useSession } from '../../lib/session.js';
import { useBrand } from '../../lib/brand.js';
import { ApiError } from '../../lib/api.js';
import { PasswordInput } from '../../components/Form.js';

type Door = 'resident' | 'owner' | 'staff' | 'admin';

interface DoorConfig {
  heading: string;
  subtitle: string;
  cardModifier: 'portal' | 'staff' | 'admin';
  crossLinks: { to: string; label: string }[];
  restrictedNote?: string;
}

/**
 * Four doors, one credential check. Every door posts to the same endpoint
 * with the same credentials — the split exists so each audience gets
 * appropriate wording and lands in the right place, NOT as a security
 * boundary. A tenant who submits the staff form still authenticates fine and
 * is then sent to the portal; door choice never overrides what `/me`
 * resolves the account to actually be.
 *
 * Resident and owner share the `portal` card styling (same orange glow) —
 * they're the same brand-level "resident/owner portal" grouping the combined
 * page used before the split. `admin` gets its own deeper glow and a note
 * about the 2FA step planned for that account type, so it visually reads as
 * the restricted door even though it authenticates identically today.
 */
const DOORS: Record<Door, DoorConfig> = {
  resident: {
    heading: 'Resident Center',
    subtitle:
      'Sign in to access your rental dashboard, pay rent, and submit requests to maintenance.',
    cardModifier: 'portal',
    crossLinks: [],
  },
  owner: {
    heading: 'Owner Center',
    subtitle: 'Sign in to your owner dashboard to manage your properties.',
    cardModifier: 'portal',
    crossLinks: [{ to: '/login', label: 'Resident' }],
  },
  staff: {
    // '{brand}' is replaced with the developer-configured product name at
    // render time (see LoginPage's useBrand() call) — resident/owner have no
    // token, so the same .replace() is a no-op for them.
    heading: '{brand} Commander',
    subtitle: 'Staff Portal',
    cardModifier: 'staff',
    crossLinks: [
      { to: '/login', label: 'Resident' },
      { to: '/login/owner', label: 'Owner' },
    ],
  },
  admin: {
    heading: '{brand} Commander',
    subtitle: '',
    cardModifier: 'admin',
    crossLinks: [{ to: '/staff/login', label: 'Staff' }],
    restrictedNote:
      'A second verification step (2FA) will be required for this account type in a future release.',
  },
};

interface Props {
  door: Door;
}

export function LoginPage({ door }: Props) {
  const { state, signIn } = useSession();
  const { product } = useBrand();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Already signed in. Previously this silently bounced to wherever that
  // actor belongs — correct for someone who followed a stale link, but a
  // trap for switching accounts: the login form never rendered, so there
  // was no way to reach it without already knowing to find "Sign out" in
  // the header first. Showing who's signed in, with sign-out inline, keeps
  // the same default (don't re-auth over an existing session unasked) while
  // making the switch a single click from the door someone actually landed
  // on.
  if (state.status === 'signed-in') {
    return <AlreadySignedIn me={state.me} door={door} />;
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    // Same schema the API validates with (packages/shared), so the two cannot
    // disagree about what a valid submission looks like.
    const parsed = loginSchema.safeParse({ email, password });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Enter an email address and password.');
      return;
    }

    setBusy(true);
    try {
      const me = await signIn(parsed.data.email, parsed.data.password);
      void navigate(me.actor.actorType === 'STAFF' ? '/staff' : '/portal', { replace: true });
    } catch (caught) {
      // One message for unknown-email and wrong-password alike. Distinguishing
      // them turns this form into an account-enumeration oracle, which for a
      // tenant portal means confirming who rents from this company.
      setError(
        caught instanceof ApiError && caught.status === 429
          ? 'Too many attempts. Wait a minute and try again.'
          : 'Those credentials did not work.',
      );
      setPassword('');
    } finally {
      setBusy(false);
    }
  }

  const config = DOORS[door];

  return (
    <div className="auth-page">
      <div className={`card card--auth card--auth--${config.cardModifier}`}>
        <h1>{config.heading.replace('{brand}', product.name)}</h1>
        <p className="muted">{config.subtitle}</p>

        {config.restrictedNote ? (
          <p className="alert alert--warn">{config.restrictedNote}</p>
        ) : null}

        {error ? (
          <p className="alert alert--error" role="alert">
            {error}
          </p>
        ) : null}

        <form onSubmit={onSubmit} noValidate>
          <label className="field">
            <span className="field__label">Email</span>
            <input
              className="field__input"
              type="email"
              name="email"
              autoComplete="username"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>

          <label className="field">
            <span className="field__label">Password</span>
            <PasswordInput
              name="password"
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
            />
          </label>

          <button className="button" type="submit" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="muted" style={{ marginTop: '1rem', fontSize: '0.85rem' }}>
          {config.crossLinks.map((link, i) => (
            <span key={link.to}>
              {i > 0 ? ' · ' : ''}
              {link.label}? <a href={link.to}>Sign in here</a>.
            </span>
          ))}
        </p>

        {/* Accounts are provisioned, not self-registered: `users.actor_type` is NOT
            NULL and CHECK-constrained to one scope key, so nothing generic could
            create a valid row. There is deliberately no "create account" link. */}
        <p className="muted" style={{ fontSize: '0.8rem' }}>
          Contact Support if you're having trouble signing in, or can't access your account.
        </p>

        <PoweredByFooter />
      </div>
    </div>
  );
}

/** Shown at the bottom of every login door, and while already signed in at the same URL. */
function PoweredByFooter() {
  const { product } = useBrand();
  return (
    <p className="muted" style={{ marginTop: '1.5rem', fontSize: '0.75rem', textAlign: 'center' }}>
      Powered by {product.name}
    </p>
  );
}

/** What a login door shows when the visitor already has a session. */
function AlreadySignedIn({ me, door }: { me: MeResponse; door: Door }) {
  const { signOut } = useSession();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const home = me.actor.actorType === 'STAFF' ? '/staff' : '/portal';
  const cardModifier = DOORS[door].cardModifier;

  async function handleSwitch() {
    setBusy(true);
    // No navigation call here — signOut() flips session state to
    // signed-out, and LoginPage re-renders the real form in place once it
    // does, on this same page.
    await signOut().finally(() => setBusy(false));
  }

  return (
    <div className="auth-page">
      <div className={`card card--auth card--auth--${cardModifier}`}>
        <h1>Already signed in</h1>
        <p className="muted">
          Signed in as <strong>{me.actor.email}</strong> (
          {describeRole(me.actor.actorType, me.actor.staffRole)}).
        </p>
        <div className="row">
          <button
            className="button"
            type="button"
            onClick={() => void navigate(home, { replace: true })}
          >
            Continue
          </button>
          <button
            className="button button--quiet"
            type="button"
            onClick={() => void handleSwitch()}
            disabled={busy}
          >
            {busy ? 'Signing out…' : 'Sign out and use different credentials'}
          </button>
        </div>

        <PoweredByFooter />
      </div>
    </div>
  );
}
